<template>
  <wrap>
    <template #sidebar>
      <div class="carts-sidebar">
        <ui-button
          class="big-button"
          :border_r="6"
          color="dark-blue"
          size="40"
          @click="newChatModal.open()"
          :is_disabled="!merchant.is_enable_chatroom || permission_mes?.chatWithCreator?.permission!==100"
        >
          <svg-icon name="pen2" /> New
        </ui-button>
        <div class="search-box" v-if="!loading">
          <Search @onenter="search" :placeholder_text="`Search creator name, tag`" />
          <tippy content="Set welcome message" placement="right" v-if="merchant.is_enable_chatroom">
            <ui-button :border_r="6" color="white" @click="welcomeMessageModal.open()">
              <svg-icon name="edit" />
            </ui-button>
          </tippy>
        </div>

        <div class="tabs-header" v-if="!loading">
          <div 
            v-for="(item, key) in list" 
            :key="key" 
            class="tab-item" 
            :class="{ active: currentTab === key }"
            @click="handleTabClick(key)"
          >
            <span class="tab-title">{{ item.title }}</span>
            <span class="tab-count" v-if="item.loaded">({{ item.total }})</span>
            <i class="unread" v-if="item.users.filter(user => user.unread_message_count !== 0).length !== 0"></i>
          </div>
        </div>

        <ui-loading class="static" v-if="loading || is_search_loading" />
        <div v-else-if="searchWord && !hasSearchResults" class="no-search-results">
          <div class="no-results-content">
            <svg-icon name="search" />
            <h3>No results found</h3>
            <p>Try adjusting your search terms or browse all creators</p>
          </div>
        </div>
        <div v-else class="users-list active">
          <div class="users" v-if="list[currentTab].loaded && list[currentTab].users.length !== 0">
            <transition-group name="merchant-list" tag="div">
              <div
                class="user"
                v-for="user in list[currentTab].users"
                :key="user.id"
                @click="openChat(user)"
                :class="{ current: user.slug === $route.params.id }"
              >
                <figure class="avatar">
                  <img :src="user.avatar" alt="avatar" />
                </figure>
                <div>
                  <h2>
                    <span class="name">{{ user.name }}</span>
                    <time v-if="currentTab === 'active' && user.latest_message">
                      {{ showDate(user.latest_message.created_at) }}
                    </time>
                    <span v-if="user.unread_message_count" class="unread-count">{{ user.unread_message_count }}</span>
                  </h2>
                  <p v-if="currentTab === 'active' && user.latest_message">
                    {{ removeHtml(user.latest_message.message_text) }}
                  </p>
                </div>
              </div>
            </transition-group>
            <div class="load-more" v-if="list[currentTab].current_page < list[currentTab].last_page">
              <el-button 
                type="primary"
                @click.stop="loadMore(currentTab)"
                :loading="list[currentTab].loadingMore"
                size="small"
              >
                Load More
              </el-button>
            </div>
          </div>
          <div v-else-if="list[currentTab].loaded && list[currentTab].users.length === 0" class="no-users-in-tab">
            <p>No creators in this tab</p>
          </div>
        </div>
      </div>
    </template>
    <template #main>
      <div class="chats-wrap" :class="{'is-disable-chat': !merchant.is_enable_chatroom}">
        <header class="chats-header" v-if="$route.name === 'messaging-chats'">
          <h2>
            <span>Chats</span>
            <queue-progress :isStatic="true" />
          </h2>
          <Alert v-if="!merchant.is_enable_chatroom" svgName="lock" className="yellow">
            <p>
              Your current plan does not have access to this feature. 
              <a v-if="$store.state.projectName !== 'cj'" :href="`mailto:${$store.state.mail_to_creator}?subject=${$store.state.user_data.merchant_account.merchant_display_name} - Upgrade to Custom Subdomain&body= Please provide your name and contact info. A representative will contact you in 1-2 business days.`" target="_blank">Contact us</a>
              <a v-else href="#" @click.prevent="EventBus.emit('open-global-drawer', 'help-support')">Contact us</a>
              to upgrade your account
            </p>
          </Alert>
        </header>
        <header class="chats-header" style="height: 72px;" v-else>
          <el-avatar :size="44" :src="currentUser.avatar" />
          <div class="chats-header-info">
            <h3>{{ currentUser.name }}</h3>
            <ul v-if="creatorDetail.merchant_account_tags">
              <li v-for="(tag, index) in creatorDetail.merchant_account_tags.slice(0, 2)" :key="tag.id">
                <svg-icon name="tag" /><span>{{ tag.tag }}</span>
              </li>
              <li v-if="creatorDetail.merchant_account_tags.length > 2" class="more-tags">
                <svg-icon name="tag" /><span>+{{ creatorDetail.merchant_account_tags.length - 2 }}</span>
              </li>
            </ul>
          </div>
          <div class="icons">
            <a class="ico" v-if="creatorDetail.share_link && creatorDetail.anchor_page_status === 100" :href="`${creatorDetail.share_link}`" target="_blank">
              <svg-icon name="storefront" />
            </a>
            <span :class="['ico', permission_mes?.sendOnlyOrDisableChat?.permission!==100 && 'not-allowed-btn']" @click="openEdit">
              <svg-icon name="edit" />
            </span>
          </div>
        </header>
        
        <div class="chats-content" v-if="$route.name === 'messaging-chats'">
          <div class="no-users">
            <div>
              <h3 v-if="merchant.is_enable_chatroom">Send a message to start a chat</h3>
              <ui-button :is_disabled="permission_mes?.chatWithCreator?.permission!==100" v-if="merchant.is_enable_chatroom" @click="newChatModal.open()" color="dark-blue" size="40" :border_r="6">
                Send Message
              </ui-button>
              <div class="get-started" v-if="merchant.is_enable_chatroom && !welcomeMessage || permission_mes.editWelcomeMessage.permission !== 0">
                <h4>Getting Started item</h4>
                <div class="msg">
                  <svg-icon name="user-list" />
                  <p>
                    <label>Welcome message</label>
                    <span>{{ welcomeMessage ? '1' : '0' }}/1</span>
                  </p>
                  <ui-button color="dark-blue" size="32" @click="welcomeMessageModal.open()" :border_r="6">{{
                    welcomeMessage ? 'Edit' : 'Add'
                  }}</ui-button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <chat-messaging
          v-if="$route.name === 'messaging-chats-detail' && token && currentUser.id"
          :user="currentUser"
          :portal_user="store.state.user_data"
          :chatroom_url="store.state.chatroom_url"
          :merchant="merchant"
          :token="token"
          :ajax="ajax"
          type="merchant"
          ref="chatRoom"
          :key="token"
          :is_disabled="isDisabledMessage"
          :deleted_announcement_ids="deletedAnnouncementIds"
          @get-message-history="getMessageHistory"
          @send-message="sendMessage"
        />
        <!-- @mark-as-read="markAsRead" -->

      </div>
    </template>
  </wrap>

  <new-chat ref="newChatModal" />
  <welcome-message ref="welcomeMessageModal" :merchant="merchant" />
  <edit-creator ref="edit" @save="saveEdit" />
</template>

<script setup>
import Wrap from '@/components/messaging/Wrap.vue';
import Search from '@/components/ui/Search.vue';
import ChatMessaging from '@/components/chat/ChatMessaging.vue';
import WelcomeMessage from '@/components/messaging/WelcomeMessage.vue';
import NewChat from '@/components/messaging/NewChat.vue';
import Alert from '@/components/settings/SettingAlert.vue';
import EditCreator from '@/components/creator/EditCreator.vue';
import log from '@/utils/Log';
import { CHATROOM_PREFIX, chatStore, mergeDeletedAnnouncements } from '@/utils/ChatConfig';

import { ElAvatar, ElButton } from 'element-plus';
import { ref, watch, getCurrentInstance, onMounted, computed } from 'vue';
import { useRouter,useRoute } from 'vue-router';
import { useStore } from 'vuex';
import moment from 'moment';
import Store from '@/store/index.js';

const router = useRouter();
const store = useStore();
const app = getCurrentInstance();
const ajax = app.appContext.config.globalProperties.$ajax;
const eventBus = app.appContext.config.globalProperties.EventBus;

// this.$store.state.user_data
const currentUser = ref({});
const merchant = ref({
  id: store.state.user_data.merchant_account_id,
  name: store.state.user_data.merchant_account.merchant_display_name,
  logo: store.state.user_data.merchant_account.brand_logo,
  is_enable_chatroom: store.state.user_data.merchant_account.is_enable_chatroom,
});
const token = ref('');
const last_active_date = ref('');
const chatRoom = ref(null);
const newChatModal = ref(null);
const creatorDetail = ref({});
const welcomeMessageModal = ref(null);
const searchWord = ref('');
const searchResult = ref([]);
const welcomeMessage = ref(false);
const loading = ref(true);
const is_search_loading = ref(false);
const isDisabledMessage = ref(false);
const edit = ref(null);
const deletedAnnouncementIds = ref([]);
const currentTab = ref('active');
const prefix = CHATROOM_PREFIX;

const list = ref({
  active: {
    title: 'Active',
    active: false,
    users: [],
    loaded: false,
    loading: false,
    loadingMore: false,
    total: 0,
    current_page: 0,
    last_page: 1,
  },
  inactive: {
    title: 'Closed',
    active: false,
    users: [],
    loaded: false,
    loading: false,
    loadingMore: false,
    total: 0,
    current_page: 0,
    last_page: 1,
  },
  muted: {
    title: 'Send-only',
    active: false,
    users: [],
    loaded: false,
    loading: false,
    loadingMore: false,
    total: 0,
    current_page: 0,
    last_page: 1,
  },
});

const permission_mes= computed(()=>{
  return Store.state.user_data?.permission_list?.messagesAndAnnouncements;
})

const hasSearchResults = computed(() => {
  if (!searchWord.value) return true;
  return Object.values(searchResult.value).some(item => item.users.length > 0);
});

const openEdit = () => {
  edit.value.open(creatorDetail.value);
};

const saveEdit = (data) => {
  // Only refresh current tab
  list.value[currentTab.value].loaded = false;
  list.value[currentTab.value].current_page = 0;
  getUsersByStatus(currentTab.value, searchWord.value, 1);
};

const toSlug = (item) => {
  // Convert ID to base64 string
  const idBase64 = btoa(item.id.toString())
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
  
  // If idBase64 is at least 8 characters, return the first 8
  if (idBase64.length >= 8) {
    return idBase64.substring(0, 8);
  }
  
  // Otherwise, encode the anchor_creator_id and append characters until we reach 8
  const nameBase64 = btoa(item.anchor_creator_id)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
  
  // Calculate how many characters we need from the name
  const remainingChars = 8 - idBase64.length;
  
  // Use as many characters as available from nameBase64, up to the required amount
  const nameChars = nameBase64.substring(0, Math.min(remainingChars, nameBase64.length));
  
  // Return the combined slug
  return idBase64 + nameChars;
};

const getUsersByStatus = (status, keyword = '', page = 1) => {
  return new Promise((resolve, reject) => {
    if (page === 1) {
      list.value[status].loading = true;
    } else {
      list.value[status].loadingMore = true;
    }
    ajax.get(`/chatroom/get-creator-list?status=${status}&page=${page}${keyword ? `&keyword=${encodeURIComponent(keyword)}` : ''}`)
      .then((res) => {
        // console.log('Status:', status, 'Page:', page, 'Data:', res.data.data);
        
        const newItems = res.data.data.data.map((item) => {
          item.slug = toSlug(item);
          item.avatar = item.avatar ? item.avatar : require('@/assets/images/user.png');

          if (router.currentRoute.value.params.id) {
            if (item.slug === router.currentRoute.value.params.id) {
              currentUser.value = item;
              isDisabledMessage.value = item.chat_status === 'inactive';
              getCreatorDetail(item.anchor_creator_id);
            }
          }
          return item;
        });

        // If first page, replace data; otherwise append data
        if (page === 1) {
          list.value[status].users = newItems;
        } else {
          list.value[status].users = [...list.value[status].users, ...newItems];
        }
        
        list.value[status].total = res.data.data.total || list.value[status].users.length;
        list.value[status].current_page = res.data.data.current_page || page;
        list.value[status].last_page = res.data.data.last_page || 1;
        list.value[status].loaded = true;
        list.value[status].loading = false;
        list.value[status].loadingMore = false;
        
        const urlSlug = router.currentRoute.value.params.id;
        if (urlSlug && list.value[status].users.find((user) => user.slug === urlSlug)) {
          list.value[status].active = true;
          getToken(urlSlug);
        }

        searchResult.value = list.value;
        resolve(res);
      })
      .catch((error) => {
        console.error(`Error fetching ${status} creators:`, error);
        list.value[status].loading = false;
        list.value[status].loadingMore = false;
        reject(error);
      });
  });
};

const loadMore = (key) => {
  const item = list.value[key];
  if (item.current_page < item.last_page && !item.loadingMore) {
    getUsersByStatus(key, searchWord.value, item.current_page + 1);
  }
};

const handleTabClick = (key) => {
  currentTab.value = key;
  const item = list.value[key];
  
  // If data has not been loaded yet, fetch data
  if (!item.loaded && !item.loading) {
    getUsersByStatus(key, searchWord.value);
  }
};

const getUsers = (keyword = '') => {
  const statuses = ['active', 'inactive', 'muted'];
  
  // First load or search needs to request all 3 tabs' first page to get total
  // Reset all tabs' state
  statuses.forEach(status => {
    list.value[status].loaded = false;
    list.value[status].current_page = 0;
    list.value[status].users = [];
  });
  
  // Request first page of all 3 tabs
  Promise.all(statuses.map(status => getUsersByStatus(status, keyword, 1)))
    .then(() => {
      // After all data loaded, check if URL slug matches any user
      checkAndRedirectInvalidSlug();
    })
    .finally(() => {
      loading.value = false;
      is_search_loading.value = false;
    });
};

// Check if URL slug is valid, if not redirect to /messaging/chats/
const checkAndRedirectInvalidSlug = () => {
  const urlSlug = router.currentRoute.value.params.id;
  if (!urlSlug) return;
  
  // Check if slug matches any user in all tabs
  let userFound = false;
  Object.keys(list.value).forEach((key) => {
    const users = list.value[key].users;
    if (users.find(user => user.slug === urlSlug)) {
      userFound = true;
    }
  });
  
  // If no user found, redirect without triggering data refresh
  if (!userFound) {
    router.replace('/messaging/chats/');
  }
};

const removeHtml = (text) => {
  return text.replace(/<[^>]*>?/g, '');
};

const showDate = (date) => {
  const local = moment.utc(date).local();

  if (moment().format('YYYY-MM-DD') === moment(local).format('YYYY-MM-DD')) 
    return moment(local).format('h:mma');
  else if (moment().format('YYYY') === moment(local).format('YYYY'))
    return moment(local).format('MM/DD');
  else 
    return moment(local).format('MM/DD/YYYY');
};

const openChat = (user) => {
  router.push('/messaging/chats/' + user.slug);
};

const getDeletedAnnouncementIds = async () => {
  const merchantId = merchant.value.id;
  const apiVersion = store.state.user_data.merchant_accounts.find((account) => account.id === merchantId)?.archived_announcement_version;
  const dataKey = prefix + 'deleted_announcement_' + merchantId;
  const versionKey = prefix + 'archived_announcement_version_' + merchantId;

  const cachedData = (await chatStore.getItem(dataKey)) || {};
  const cachedVersion = await chatStore.getItem(versionKey);

  // Version unchanged: reuse cached data, no request.
  if (cachedVersion != null && Number(cachedVersion) === Number(apiVersion)) {
    deletedAnnouncementIds.value = cachedData;
    return;
  }

  let api = '/chatroom/get-deleted-chatroom-announcement-list';
  api += '?latest_archive_announcement_version=' + apiVersion;
  if (cachedVersion != null) {
    api += '&last_archive_announcement_version=' + cachedVersion;
  }

  ajax.get(api).then((res) => {
    const hiddenAnnouncements = res.data.data.hidden_announcements || [];
    const archivedAnnouncements = res.data.data.archived_announcements || [];
    res.data.data.hidden_announcements = hiddenAnnouncements.concat(archivedAnnouncements);
    delete res.data.data.archived_announcements;

    // Merge the newly fetched delta into the cached data.
    const merged = mergeDeletedAnnouncements(cachedData, res.data.data);
    deletedAnnouncementIds.value = merged;
    chatStore.setItem(dataKey, merged);
    chatStore.setItem(versionKey, apiVersion);
  });
};

const getToken = (slug) => {
  let userId = null;
  
  Object.keys(list.value).forEach((key) => {
    const users = list.value[key].users;
    const user = users.find(u => u.slug === slug);
    if (user) {
      userId = user.id;
    }
  });
  
  if (!userId) {
    console.error('User not found for slug:', slug);
    return;
  }
  
  ajax.post('/chatroom/get-auth-key', { creator_portal_user_id: userId.toString() }).then((res) => {
    token.value = res.data.data.auth_key;
    last_active_date.value = res.data.data.creator_last_active_time;
  });
};

const getWelcomeMessage = () => {
  ajax.get('/chatroom/get-welcome-message').then((res) => {
    if (res.data.data) {
      welcomeMessage.value = true;
    }
  });
};

const sendMessage = (msg) => {
  // console.log('sendMessage', msg);
  // const currentMerchantIndex = list.value.findIndex(item => item.active);
  
  // if (currentMerchantIndex > 0) {
  //   const currentMerchant = list.value.splice(currentMerchantIndex, 1)[0];
  //   list.value.unshift(currentMerchant);
  // }
  const currentMerchantIndex = list.value.active.users.findIndex(item => item.id === currentUser.value.id);
  if (currentMerchantIndex > 0) {
    const currentMerchant = list.value.active.users.splice(currentMerchantIndex, 1)[0];
    list.value.active.users.unshift(currentMerchant);
  }
};

const getMessageHistory = (id = '') => {
  if (!currentUser.value || !currentUser.value.id) {
    console.error('Current user not found');
    return;
  }
  
  let api = '/chatroom/get-message-history?creator_portal_user_id=' + currentUser.value.id;

  
  if (id) {
    api += '&last_message_id=' + id;
  }

  ajax.get(api).then((res) => {
    markAsRead();
    chatRoom.value.syncMessages(res.data.data);
  });
};

const markAsRead = () => {
  const id = currentUser.value.id;
  Object.keys(list.value).forEach((key) => {
    const users = list.value[key].users;
    users.forEach((user) => {
      if (user.id === Number(id)) {
        store.state.unread_message_count = store.state.unread_message_count - user.unread_message_count;
        user.unread_message_count = 0;
        eventBus.emit('update-unread-message-count', user.unread_message_count);
      }
    });
  });

};

const search = (word) => {
  searchWord.value = word;
  is_search_loading.value = true;
  getUsers(word);
};

const getCreatorDetail = (id) => {
  ajax.get('/creator/get-creator-detail?creator_user_id=' + id).then((res) => {
    creatorDetail.value = res.data.data;
  });
};

// Removed frontend search filter logic, now using ajax search
// watch(searchWord, (word) => {
//   if (word) {
//     const obj = JSON.parse(JSON.stringify(list.value));
//     for (let key in obj) {
//       obj[key].users = obj[key].users.filter((user) => {
//         return user.name.toLowerCase().includes(word.toLowerCase());
//       });
//     }

//     searchResult.value = obj;
//   } else {
//     searchResult.value = list.value;
//   }
// });

watch(
  () => router.currentRoute.value.params.id,
  (slug) => {
    if (slug) {
      let userFound = false;
      Object.keys(list.value).forEach((key) => {
        const users = list.value[key].users;
        const user = users.find(user => user.slug === slug);
        
        if (user) {
          userFound = true;
          currentUser.value = user;
          isDisabledMessage.value = user.chat_status === 'inactive';
          getToken(slug);
          getCreatorDetail(user.anchor_creator_id);
        }
      });
      
      // If all tabs are loaded but no user found, redirect
      const allLoaded = Object.values(list.value).every(item => item.loaded);
      if (allLoaded && !userFound) {
        router.replace('/messaging/chats/');
      }
    }
  }
);

onMounted(() => {
  if(permission_mes.value?.manageMessaging.permission === 0){
    router.push('/messaging/announcements');
  }

  log('BlueViolet', 'user data', store.state.user_data);
  getWelcomeMessage();
  getUsers(searchWord.value);
  getDeletedAnnouncementIds();
});
</script>

<style lang="scss">
.chats-wrap {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;

  .message-wrap {
    max-height: calc(100vh - 76px);

    .messaging-sidebar {
      overflow: hidden;
    }

  }

  &.is-disable-chat {
    .no-messages h3 {
      display: none;
    }
  }
}


.chats-header {
  display: flex;
  padding: 0;
  align-items: center;
  min-height: 58px;

  h2 {
    flex: 1;
    display: flex;
    align-items: center;

    & > span {
      font: var(--font-demi-24);
      color: var(--black-90);
    }
  }

  .ui-alert {
    margin: 0;
  }

  .chats-header-info {
    padding-left: 12px;
    flex: 1;
    
    h3 {
      font: var(--font-demi-20);
      line-height: 1;
      color: var(--black-90);
    }

    ul {
      display: flex;
      gap: 6px;
      padding: 4px 0 0;

      li {
        font-size: 12px;
        color: var(--black-90);
        padding: 3px 6px;
        border-radius: 4px;
        background-color: var(--black-10);
        display: flex;
        align-items: center;
        gap: 2px;

        svg {
          height: 12px;
          width: 12px;
          fill: var(--black-75);
        }
        
        
      }
    }
  }

  .icons {
    display: flex;
    gap: 4px;

    .ico {
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      height: 28px;
      width: 28px;
      cursor: pointer;
      transition: all 0.2s ease;
      border-radius: 4px;

      svg {
        height: 14px;
        width: 14px;
        fill: var(--black-75);
      }

      &:hover {
        background-color: var(--Neu-20);
        color: var(--dark-blue);
      }
    }

    .not-allowed-btn{
      cursor: not-allowed;
      pointer-events: none;
      opacity: .5;
    }
  }
}

.carts-sidebar {
  padding: 8px 0 0;

  .big-button {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 16px;

    svg {
      height: 18px;
      width: 18px;
      margin-right: 8px;
    }

    &:hover {
      border-color: var(--PB-80);
      background-color: var(--PB-80);
    }

    &.disabled {
      border-color: var(--neutral);
      color: var(--black-40);
    }
  }

  .search-box {
    display: flex;
    margin: 12px 16px 4px;

    .ui-search-input {
      flex: 1;
      margin-right: 8px;
    }

    .ui-button {
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 16px;
      color: var(--black-90);
      transition: all 0.2s ease;
      padding: 0 10px;

      svg {
        height: 14px;
        width: 14px;
        margin: 0;
      }

      &:hover {
        color: var(--dark-blue);
      }
    }
  }

  .users-list {
    // border-radius: 6px;
    // border: 1px solid var(--black-20);
    overflow: hidden;

    .users {
      position: relative;
      max-height: calc(100vh - 232px);
      overflow: auto;
      padding: 0 16px 16px;

      & > div:first-child .user:first-child {
        border-top: none;
      }

      .user {
        display: flex;
        align-items: center;
        padding: 0 8px;
        cursor: pointer;
        height: 48px;
        position: relative;
        // border-top: 1px solid var(--black-10);

        &:first-child {
          z-index: 3;
          border-top: none;
        }

        .avatar {
          width: 30px;
          min-width: 30px;
          height: 30px;
          border-radius: 50%;
          overflow: hidden;
          margin-right: 10px;
          box-sizing: content-box;
          border: 1px solid var(--black-20);

          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
        }

        div {
          width: calc(100% - 40px);

          & > p {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            font-size: 12px;
            color: var(--black-50);
          }
        }

        h2 {
          display: flex;
          align-items: center;
          
          time {
            font-size: 11px;
            color: var(--black-50);
            margin-left: 4px;
          }
        }

        .name {
          font: var(--font-med-16);
          color: var(--black-90);
          font: var(--font-med-14);
          flex: 1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        &:hover {
          background-color: var(--bg-10);
        }

        .unread-count {
          color: transparent;
          margin-left: 8px;
          border-radius: 50%;
          height: 8px;
          width: 8px;
          overflow: hidden;
          background-color: var(--blue);
        }

        &.current {
          background-color: var(--Neu-20);
        }
      }

    }

    .load-more {
      // border-top: 1px solid var(--black-10);
      padding: 8px 16px 16px;
      text-align: center;

    }
  }
}

.chats-content {
  flex: 1;
  overflow-y: auto;
  background-color: var(--bg-10);
}

.no-users {
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  height: 100%;
  padding: 0 16px;
  color: var(--black-90);
  max-width: 700px;
  margin: 0 auto;

  h3 {
    font: var(--font-med-30);
    margin-bottom: 16px;
  }

  .get-started {
    border-radius: 10px;
    background-color: var(--white);
    box-shadow: var(--bot-2dp);
    margin: 24px 0;
    padding: 16px;
    min-width: 328px;

    h4 {
      font: var(--font-med-16);
      margin-bottom: 10px;
      color: var(--black-90);
      text-align: left;
    }
  }

  .msg {
    border-radius: 8px;
    box-shadow: 0 1px 2px 1px rgba(0, 0, 0, 0.1);
    padding: 16px;
    display: flex;
    align-items: center;

    svg {
      height: 42px;
      width: 42px;
      fill: var(--dark-blue);
      margin-right: 10px;
    }

    p {
      flex: 1;
      text-align: left;

      label {
        display: block;
        font: var(--font-med-14);
        color: var(--black-90);
      }

      span {
        font: var(--font-med-12);
        color: var(--black-50);
      }
    }
  }
}

.merchant-list-move,
.merchant-list-enter-active,
.merchant-list-leave-active {
  transition: all 0.5s ease;
}

.merchant-list-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.merchant-list-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.merchant-list-move {
  transition: transform 0.5s ease;
}

.no-search-results {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 16px;
  
  .no-results-content {
    text-align: center;
    max-width: 300px;
    
    svg {
      height: 42px;
      width: 42px;
      fill: var(--black-40);
      margin: 0 auto 16px;
    }
    
    h3 {
      font: var(--font-med-16);
      color: var(--black-90);
      margin-bottom: 6px;
    }
    
    p {
      color: var(--black-60);
      line-height: 1.4;
    }
  }
}

.tabs-header {
  display: flex;
  margin: 12px 16px 8px;
  gap: 8px;
  // margin-bottom: 12px;

  .tab-item {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    cursor: pointer;
    gap: 4px;
    border: 1px solid white;
    border-radius: var(--radius);

    .tab-title {
      font: var(--font-med-14);
      color: var(--black-90);
    }

    .tab-count {
      font: var(--font-med-12);
    }

    .unread {
      border-radius: 50%;
      height: 6px;
      width: 6px;
      background-color: var(--red);
    }

    &:hover {
      border-color: var(--Neu-10);
      background-color: var(--Neu-10);

      .tab-title {
        color: var(--black-90);
      }
    }

    &.active {
      border-color: var(--black-40) !important;
      background-color: white !important;

      .tab-title {
        color: var(--black-90);
      }
    }
  }
}

.no-users-in-tab {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 16px;
  color: var(--black-50);
  font: var(--font-med-14);
}
</style>
